import {
  OTPInput,
  REGEXP_ONLY_CHARS,
  REGEXP_ONLY_DIGITS_AND_CHARS,
  REGEXP_ONLY_DIGITS,
  type SlotProps,
} from 'input-otp';
import { twMerge } from 'tailwind-merge';
import { type ExtendedSlotProps, type LiliOtpProps, LiliOtpType } from './LiliOtp.types';
import { LiliError } from '../LiliError';
import { getFontClass } from '../../tokens';

const FakeCaret = ({ error }: { error: boolean | undefined }) => {
  return (
    <div className='absolute pointer-events-none bottom-[11px] flex items-center animate-caretBlink'>
      <div
        className={`w-[23px] h-[1.5px] ${error ? 'bg-text-negative-red' : 'bg-text-brand-primary'}`}
      />
    </div>
  );
};

const Slot = (props: ExtendedSlotProps) => {
  const { char, isActive, error } = props;
  const hasValue = char !== null;

  return (
    <div
      className={twMerge(
        `w-[43px] h-[58px] text-[2rem] text-text-brand-primary flex items-center justify-center relative ${getFontClass()}`,
        'bg-surface-primary-bw-02 rounded-[4px] transition-all duration-200 border-[1.5px] border-outline-primary-bw-03 hover:border-outline-surface-primary-bw-05',
        (hasValue || isActive) &&
          'border-[1.5px] border-outline-brand-primary  bg-surface-primary-bw-00',
        isActive && !error && 'shadow-otpShadow',
        error &&
          'border-[1.5px] border-outline-negative-primary bg-surface-negative-tertiary text-text-main-secondary'
      )}>
      {hasValue && <div>{char}</div>}
      {isActive && <FakeCaret error={error} />}
    </div>
  );
};

export const LiliOtp = (props: LiliOtpProps) => {
  const {
    maxLength,
    onCompleted,
    id,
    pattern,
    type = LiliOtpType.NUMERIC,
    disabled,
    errorMessage,
    onBlur,
    onFocus,
    onChange,
    value,
    autoFocus,
    className,
    ...rest
  } = props;

  const getPatternByType = (type: LiliOtpType) => {
    switch (type) {
      case LiliOtpType.NUMERIC:
        return REGEXP_ONLY_DIGITS;
      case LiliOtpType.ALPHABETIC:
        return REGEXP_ONLY_CHARS;
      case LiliOtpType.ALPHANUMERIC:
        return REGEXP_ONLY_DIGITS_AND_CHARS;
      case LiliOtpType.CUSTOM:
        return pattern;
    }
  };

  return (
    <OTPInput
      {...rest}
      onComplete={onCompleted}
      maxLength={maxLength}
      disabled={disabled}
      containerClassName='group flex items-center has-[:disabled]:opacity-50'
      id={id}
      pattern={getPatternByType(type)}
      onBlur={onBlur}
      onChange={onChange}
      onFocus={onFocus}
      value={value}
      inputMode='email'
      autoFocus={autoFocus}
      data-hj-allow
      render={({ slots }: { slots: SlotProps[] }) => (
        <div className={className}>
          <div className='flex flex-row gap-[16px] mx-auto w-fit'>
            {slots.map((slot: SlotProps, idx: number) => (
              <Slot key={idx} {...slot} error={!!errorMessage} />
            ))}
          </div>
          <LiliError
            id={`${id}-error`}
            errorMessage={errorMessage}
            className='justify-center mt-[6px]'
          />
        </div>
      )}
    />
  );
};
